import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import classes from "../../TabStyles/DashboardTab.module.css";

const THEME = {
  background: "#F8FAFC",
  surface: "#FFFFFF",
  text: "#0F172A",
  muted: "#64748B",
  border: "rgba(226,232,240,0.8)",
  blue: "#2563EB",
  cyan: "#0EA5E9",
  green: "#10B981",
  amber: "#F59E0B",
  red: "#EF4444",
  purple: "#7C3AED",
};

const MENU = [
  {
    id: "dashboard1",
    label: "Purchase Request Analytics",
    description: "PR status, approvals, spend and allocation",
    accent: THEME.blue,
  },
  {
    id: "dashboard2",
    label: "3 Way Matching Analytics",
    description: "Invoice matching health and recent runs",
    accent: THEME.purple,
  },
  {
    id: "dashboard3",
    label: "Purchase Order Analytics",
    description: "PO value, approval and supplier breakdown",
    accent: THEME.green,
  },
];

const PALETTE = [THEME.blue, THEME.cyan, THEME.green, THEME.amber, THEME.purple, THEME.red];
const MONTH_OPTIONS = [
  { value: "all", label: "All Months" },
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

const toNumber = (value) => {
  if (typeof value === "number") return value;
  const number = Number(String(value ?? "").replace(/,/g, "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(number) ? number : 0;
};

const formatNumber = (value) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(toNumber(value));
const formatCurrency = (value) => `\u20B9${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(toNumber(value))}`;
const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};
const clean = (value) => String(value ?? "-").trim() || "-";
const getYearFromDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) return String(date.getFullYear());

  const match = String(value).match(/\b(20\d{2}|19\d{2})\b/);
  return match ? match[1] : "";
};
const getYearFromPeriod = (value) => {
  const match = String(value || "").match(/\b(20\d{2}|19\d{2})\b/);
  return match ? match[1] : "";
};
const monthLabelByValue = MONTH_OPTIONS.reduce((result, month) => {
  result[month.value] = month.label;
  return result;
}, {});

const Card = ({ title, children }) => (
  <section style={{ background: "linear-gradient(145deg, #FFFFFF 0%, #FBFDFF 100%)", border: "1px solid rgba(203,213,225,0.72)", borderRadius: 22, padding: 24, boxShadow: "0 10px 32px rgba(15,23,42,0.065)", minWidth: 0, position: "relative", overflow: "hidden" }}>
    {title ? <h2 style={{ margin: "0 0 18px", paddingBottom: 13, borderBottom: "1px solid rgba(148,163,184,0.18)", color: THEME.text, fontSize: 18, fontFamily: '"Gilroy-SemiBold", "Gilroy", "Segoe UI", sans-serif', fontWeight: 600, letterSpacing: "-0.01em" }}>{title}</h2> : null}
    {children}
  </section>
);

const DefaultMetricTooltip = ({ title, value, note }) => (
  <div style={{ display: "grid", gap: 9 }}>
    <div>
      <div style={{ color: THEME.muted, fontSize: 11, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase" }}>{title}</div>
      <div style={{ color: THEME.text, fontSize: 15, fontWeight: 700, marginTop: 2 }}>{value} total</div>
      <div style={{ color: THEME.muted, fontSize: 12, marginTop: 1 }}>Year-wise breakdown</div>
    </div>
    <div style={{ display: "grid", gap: 6 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20, padding: "7px 9px", borderRadius: 10, background: "#F8FAFC" }}>
        <span style={{ color: "#334155", fontSize: 13, fontWeight: 600 }}>All years</span>
        <span style={{ color: THEME.text, fontSize: 13, fontWeight: 700, whiteSpace: "nowrap" }}>{value}</span>
      </div>
      {note ? <div style={{ color: THEME.muted, fontSize: 12 }}>{note}</div> : null}
    </div>
  </div>
);

const MetricCard = ({ title, value, note, accent = THEME.blue, tooltip, onClick }) => {
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const tooltipContent = tooltip || <DefaultMetricTooltip title={title} value={value} note={note} />;
  const isTooltipOpen = Boolean(tooltipContent) && (hovered || pinned);
  const isClickable = typeof onClick === "function";

  const handleClick = () => {
    if (isClickable) {
      onClick();
      return;
    }

    if (tooltipContent) {
      setPinned((current) => !current);
    }
  };

  const handleKeyDown = (event) => {
    if (!isClickable) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <div
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      style={{ background: `linear-gradient(145deg, ${accent}10 0%, #FFFFFF 58%)`, border: `1px solid ${accent}25`, borderRadius: 20, minHeight: 118, padding: "20px 20px 18px", boxShadow: `0 9px 26px ${accent}14`, position: "relative", overflow: "visible", cursor: isClickable || tooltipContent ? "pointer" : "default", zIndex: isTooltipOpen ? 20 : 1 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <div style={{ position: "absolute", inset: "0 0 auto", height: 4, background: `linear-gradient(90deg, ${accent}, rgba(255,255,255,0.55))`, borderRadius: "20px 20px 0 0" }} />
      <div style={{ position: "absolute", width: 58, height: 58, borderRadius: "50%", right: -18, top: 12, background: `${accent}0D`, pointerEvents: "none" }} />
      <div style={{ color: "#334155", fontSize: 15, fontWeight: 600 }}>{title}</div>
      <div style={{ color: THEME.text, fontSize: "clamp(20px, 1.5vw, 28px)", fontWeight: 700, marginTop: 8, overflowWrap: "anywhere" }}>{value}</div>
      {note ? <div style={{ color: THEME.muted, fontSize: 13, marginTop: 8 }}>{note}</div> : null}
      {tooltipContent ? (
        <div
          onClick={(event) => event.stopPropagation()}
          style={{ position: "absolute", top: "calc(100% + 10px)", left: 12, width: "max-content", minWidth: 250, maxWidth: "min(360px, calc(100vw - 48px))", maxHeight: 280, overflowY: "auto", padding: "13px 14px", border: `1px solid ${THEME.border}`, borderRadius: 14, background: "#fff", boxShadow: "0 18px 36px rgba(15,23,42,0.16)", opacity: isTooltipOpen ? 1 : 0, transform: isTooltipOpen ? "translateY(0)" : "translateY(-4px)", pointerEvents: isTooltipOpen ? "auto" : "none", transition: "opacity 160ms ease, transform 160ms ease", zIndex: 30 }}
        >
          {tooltipContent}
        </div>
      ) : null}
    </div>
  );
};

const MetricGrid = ({ items }) => (
  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 14 }}>
    {items.map((item) => <MetricCard key={item.title} {...item} />)}
  </div>
);

const YearWisePrTooltip = ({ title, total, rows = [], valueKey, formatter = formatNumber }) => (
  <div style={{ display: "grid", gap: 9 }}>
    <div>
      <div style={{ color: THEME.muted, fontSize: 11, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase" }}>{title}</div>
      <div style={{ color: THEME.text, fontSize: 15, fontWeight: 700, marginTop: 2 }}>{formatter(total)} total</div>
      <div style={{ color: THEME.muted, fontSize: 12, marginTop: 1 }}>Year-wise breakdown</div>
    </div>
    <div style={{ display: "grid", gap: 6 }}>
      {Array.isArray(rows) && rows.length ? rows.map((row) => (
        <div key={`${row?.year}-${valueKey}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20, padding: "7px 9px", borderRadius: 10, background: "#F8FAFC" }}>
          <span style={{ color: "#334155", fontSize: 13, fontWeight: 600 }}>{clean(row?.year)}</span>
          <span style={{ color: THEME.text, fontSize: 13, fontWeight: 700, whiteSpace: "nowrap" }}>{formatter(row?.[valueKey])}</span>
        </div>
      )) : <div style={{ color: THEME.muted, fontSize: 13 }}>No yearly PR data available.</div>}
    </div>
  </div>
);

const YearWiseSummaryTooltip = ({ title, total, rows = [], valueKey = "count", formatter = formatNumber }) => (
  <div style={{ display: "grid", gap: 9 }}>
    <div>
      <div style={{ color: THEME.muted, fontSize: 11, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase" }}>{title}</div>
      <div style={{ color: THEME.text, fontSize: 15, fontWeight: 700, marginTop: 2 }}>{formatter(total)} total</div>
      <div style={{ color: THEME.muted, fontSize: 12, marginTop: 1 }}>Year-wise breakdown</div>
    </div>
    <div style={{ display: "grid", gap: 6 }}>
      {Array.isArray(rows) && rows.length ? rows.map((row) => (
        <div key={`${row?.year}-${valueKey}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20, padding: "7px 9px", borderRadius: 10, background: "#F8FAFC" }}>
          <span style={{ color: "#334155", fontSize: 13, fontWeight: 600 }}>{clean(row?.year)}</span>
          <span style={{ color: THEME.text, fontSize: 13, fontWeight: 700, whiteSpace: "nowrap" }}>{formatter(row?.[valueKey])}</span>
        </div>
      )) : <div style={{ color: THEME.muted, fontSize: 13 }}>No yearly data available.</div>}
    </div>
  </div>
);

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "#fff", border: `1px solid ${THEME.border}`, borderRadius: 12, padding: "10px 12px", boxShadow: "0 12px 30px rgba(15,23,42,0.12)" }}>
      <div style={{ color: THEME.muted, fontSize: 12 }}>{clean(label || payload[0]?.payload?.label)}</div>
      {payload.map((entry) => <div key={entry.dataKey} style={{ color: entry.color || THEME.text, fontWeight: 600 }}>{entry.name}: {formatNumber(entry.value)}</div>)}
    </div>
  );
};

const BreakdownChart = ({ title, rows = [], labelKey, valueKey = "count", valueName = "Count", accent = THEME.blue }) => {
  const data = useMemo(() => (Array.isArray(rows) ? rows : [])
    .map((row) => ({ label: clean(row?.[labelKey]), value: toNumber(row?.[valueKey]) }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 10), [rows, labelKey, valueKey]);

  return (
    <Card title={title}>
      {data.length ? (
        <div style={{ width: "100%", height: 330 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 8, right: 10, left: 0, bottom: 58 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
              <XAxis dataKey="label" interval={0} angle={-28} textAnchor="end" height={72} tick={{ fill: THEME.muted, fontSize: 11 }} />
              <YAxis tick={{ fill: THEME.muted, fontSize: 12 }} width={54} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="value" name={valueName} fill={accent} radius={[7, 7, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : <EmptyState />}
    </Card>
  );
};

const StatusChart = ({ title, rows = [] }) => {
  const getStatusColor = (status, index) => {
    const normalized = String(status || "").toLowerCase();
    if (normalized.includes("approv") || normalized.includes("paid") || normalized.includes("match")) return THEME.green;
    if (normalized.includes("pend") || normalized.includes("progress") || normalized.includes("draft")) return THEME.amber;
    if (normalized.includes("reject") || normalized.includes("fail") || normalized.includes("cancel")) return THEME.red;
    return PALETTE[index % PALETTE.length];
  };
  const data = (Array.isArray(rows) ? rows : []).filter((row) => toNumber(row?.count) > 0).map((row, index) => ({ ...row, color: getStatusColor(row?.status, index) }));
  const total = data.reduce((sum, row) => sum + toNumber(row.count), 0);
  return (
    <Card title={title}>
      {data.length ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", alignItems: "center", gap: 16, minHeight: 275, padding: "8px 0", minWidth: 0 }}>
          <div style={{ width: "100%", maxWidth: 260, height: 245, justifySelf: "center", position: "relative", borderRadius: "50%", background: "radial-gradient(circle, rgba(99,102,241,0.06) 0%, rgba(255,255,255,0) 68%)" }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="count" nameKey="status" innerRadius={67} outerRadius={96} paddingAngle={4} cornerRadius={8} stroke="none">
                  {data.map((row, index) => <Cell key={`${row.status}-${index}`} fill={row.color} />)}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ position: "absolute", inset: 0, display: "grid", placeContent: "center", textAlign: "center", pointerEvents: "none" }}>
              <div style={{ fontSize: 28, lineHeight: 1, fontWeight: 700, color: THEME.text }}>{formatNumber(total)}</div>
              <div style={{ color: THEME.muted, fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", marginTop: 6 }}>TOTAL PR</div>
            </div>
          </div>
          <div style={{ display: "grid", gap: 10, minWidth: 0 }}>
            {data.map((row) => {
              const percentage = total ? (toNumber(row.count) / total) * 100 : 0;
              return <div key={row.status} style={{ padding: "12px 13px", borderRadius: 13, background: `${row.color}0D`, border: `1px solid ${row.color}22` }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}><span style={{ color: THEME.text, fontSize: 13, fontWeight: 600, textTransform: "capitalize" }}><i style={{ display: "inline-block", width: 9, height: 9, borderRadius: "50%", background: row.color, boxShadow: `0 0 0 4px ${row.color}18`, marginRight: 9 }} />{clean(row.status)}</span><strong style={{ color: row.color, fontSize: 17 }}>{formatNumber(row.count)}</strong></div>
                <div style={{ height: 5, borderRadius: 999, background: "rgba(148,163,184,0.16)", overflow: "hidden", marginTop: 9 }}><div style={{ width: `${percentage}%`, height: "100%", borderRadius: 999, background: `linear-gradient(90deg, ${row.color}, ${row.color}AA)` }} /></div>
                <div style={{ color: THEME.muted, fontSize: 10, textAlign: "right", marginTop: 4 }}>{formatNumber(percentage)}%</div>
              </div>;
            })}
          </div>
        </div>
      ) : <EmptyState />}
    </Card>
  );
};

const LocationApprovalChart = ({ rows = [] }) => {
  const data = useMemo(() => {
    const locations = (Array.isArray(rows) ? rows : []).reduce((result, row) => {
      const approved = toNumber(row?.approved_by_me?.count);
      const pending = toNumber(row?.pending_by_me?.count);
      const rejected = toNumber(row?.rejected_by_me?.count);
      const rawLocation = String(row?.location || "");
      const city = rawLocation.split(",").map((part) => part.trim()).find((part) => part && !["null", "unknown"].includes(part.toLowerCase())) || "Unspecified";
      const key = city.toLowerCase();
      const current = result[key] || { location: city, approved: 0, pending: 0, rejected: 0, value: 0 };
      current.approved += approved;
      current.pending += pending;
      current.rejected += rejected;
      current.value += toNumber(row?.approved_by_me?.value)
        + toNumber(row?.pending_by_me?.value)
        + toNumber(row?.rejected_by_me?.value);
      result[key] = current;
      return result;
    }, {});

    return Object.values(locations)
      .map((location) => ({ ...location, total: location.approved + location.pending + location.rejected }))
      .filter((location) => location.total > 0)
      .sort((a, b) => b.total - a.total);
  }, [rows]);

  if (!data.length) return null;
  return (
    <Card>
      <div style={{ fontFamily: '"Gilroy-Regular", "Gilroy", "Segoe UI", sans-serif' }}>
      <div style={{ position: "relative", overflow: "hidden", borderRadius: 17, padding: "20px 22px", marginBottom: 16, color: "#FFFFFF", background: "linear-gradient(120deg, #2563EB 0%, #6D28D9 58%, #9333EA 100%)", boxShadow: "0 12px 28px rgba(79,70,229,0.22)" }}>
        <div style={{ position: "absolute", width: 150, height: 150, borderRadius: "50%", background: "rgba(255,255,255,0.10)", right: -35, top: -85 }} />
        <div style={{ position: "absolute", width: 90, height: 90, borderRadius: "50%", background: "rgba(255,255,255,0.08)", right: 100, bottom: -65 }} />
        <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 44, height: 44, display: "grid", placeItems: "center", borderRadius: 13, background: "rgba(255,255,255,0.17)", border: "1px solid rgba(255,255,255,0.20)", fontSize: 21 }}>⌖</div>
          <div><h2 style={{ margin: 0, color: "#FFFFFF", fontSize: 19, fontFamily: '"Gilroy-SemiBold", "Gilroy", "Segoe UI", sans-serif', fontWeight: 600 }}>Location-wise Approval Overview</h2><div style={{ color: "rgba(255,255,255,0.78)", fontSize: 13, marginTop: 4, fontWeight: 400 }}>Approval workload and PR value by requester city</div></div>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(310px, 1fr))", gap: 14 }}>
        {data.map((location, index) => (
          <div key={location.location} style={{ position: "relative", overflow: "hidden", border: `1px solid ${index === 0 ? "#C7D2FE" : THEME.border}`, borderRadius: 18, padding: 18, background: index === 0 ? "linear-gradient(145deg, #EEF2FF 0%, #FFFFFF 60%)" : "linear-gradient(145deg, #F8FAFC 0%, #FFFFFF 65%)", boxShadow: index === 0 ? "0 10px 26px rgba(79,70,229,0.12)" : "0 6px 20px rgba(15,23,42,0.06)" }}>
            <div style={{ position: "absolute", width: 100, height: 100, borderRadius: "50%", right: -45, top: -50, background: index === 0 ? "rgba(124,58,237,0.08)" : "rgba(14,165,233,0.07)" }} />
            <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <div><div style={{ color: THEME.muted, fontSize: 11, fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase" }}>Location {index + 1}</div><div style={{ color: THEME.text, fontSize: 17, fontFamily: '"Gilroy-SemiBold", "Gilroy", "Segoe UI", sans-serif', fontWeight: 600, marginTop: 3 }}>{location.location}</div></div>
              <div style={{ width: 34, height: 34, display: "grid", placeItems: "center", borderRadius: 11, background: index === 0 ? "linear-gradient(135deg, #2563EB, #7C3AED)" : "linear-gradient(135deg, #0EA5E9, #2563EB)", color: "#FFFFFF", fontWeight: 800 }}>{index + 1}</div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "150px minmax(135px, 1fr)", alignItems: "center", gap: 8, marginTop: 10 }}>
              <div style={{ width: 150, height: 150, position: "relative" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={[["Approved", location.approved, THEME.green], ["Pending", location.pending, THEME.amber], ["Rejected", location.rejected, THEME.red]].filter((item) => item[1] > 0).map(([status, count]) => ({ status, count }))} dataKey="count" nameKey="status" innerRadius={45} outerRadius={65} paddingAngle={3} cornerRadius={6} stroke="none">
                      {[["Approved", location.approved, THEME.green], ["Pending", location.pending, THEME.amber], ["Rejected", location.rejected, THEME.red]].filter((item) => item[1] > 0).map(([status, , color]) => <Cell key={status} fill={color} />)}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ position: "absolute", inset: 0, display: "grid", placeContent: "center", textAlign: "center", pointerEvents: "none" }}><strong style={{ color: THEME.text, fontSize: 22 }}>{formatNumber(location.total)}</strong><span style={{ color: THEME.muted, fontSize: 10 }}>TOTAL</span></div>
              </div>
              <div style={{ display: "grid", gap: 8 }}>
                {[["Approved", location.approved, THEME.green, "#ECFDF5"], ["Pending", location.pending, THEME.amber, "#FFFBEB"], ["Rejected", location.rejected, THEME.red, "#FEF2F2"]].map(([label, count, color, background]) => (
                  <div key={label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", borderRadius: 10, background, color: THEME.muted, fontSize: 12 }}><span><i style={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%", background: color, marginRight: 6 }} />{label}</span><b style={{ color, fontSize: 14 }}>{formatNumber(count)}</b></div>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, borderTop: `1px solid ${THEME.border}`, paddingTop: 12, marginTop: 9 }}>
              <span style={{ color: THEME.muted, fontSize: 12, fontWeight: 600 }}>Total PR value</span><strong style={{ color: "#6D28D9", fontSize: 16 }}>{formatCurrency(location.value)}</strong>
            </div>
          </div>
        ))}
      </div>
      </div>
    </Card>
  );
};

const ApproverTimeChart = ({ rows = [] }) => {
  const data = useMemo(() => (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      name: clean(row?.approver_name),
      designation: clean(row?.designation),
      email: clean(row?.email),
      count: toNumber(row?.count),
      averageSeconds: toNumber(row?.average_seconds),
      averageMinutes: toNumber(row?.average_minutes),
      averageHours: toNumber(row?.average_hours),
    }))
    .sort((a, b) => b.averageSeconds - a.averageSeconds), [rows]);

  const ApproverTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const approver = payload[0]?.payload || {};
    return (
      <div style={{ maxWidth: 280, background: "#fff", border: `1px solid ${THEME.border}`, borderRadius: 12, padding: "11px 13px", boxShadow: "0 12px 30px rgba(15,23,42,0.12)" }}>
        <div style={{ color: THEME.text, fontSize: 14, fontWeight: 700 }}>{approver.name}</div>
        <div style={{ color: THEME.muted, fontSize: 12, marginTop: 2 }}>{approver.designation}</div>
        <div style={{ color: THEME.muted, fontSize: 12, overflowWrap: "anywhere" }}>{approver.email}</div>
        <div style={{ marginTop: 9, color: THEME.purple, fontSize: 13, fontWeight: 700 }}>Average: {formatNumber(approver.averageMinutes)} min ({formatNumber(approver.averageSeconds)} sec)</div>
        <div style={{ marginTop: 3, color: THEME.text, fontSize: 13 }}>Completed approvals: {formatNumber(approver.count)}</div>
      </div>
    );
  };

  return (
    <Card title="User-wise Approval Time">
      <div style={{ color: THEME.muted, fontSize: 13, margin: "-8px 0 14px" }}>
        Average time taken by each approver to complete assigned PR approvals.
      </div>
      {data.length ? (
        <div style={{ width: "100%", height: 340, borderRadius: 16, padding: "10px 8px 0", background: "linear-gradient(180deg, #FAF5FF 0%, #FFFFFF 100%)", border: "1px solid #F0E7FF" }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 16, right: 24, left: 8, bottom: 46 }}>
              <defs>
                <linearGradient id="approverTimeFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={THEME.purple} stopOpacity={0.34} />
                  <stop offset="55%" stopColor={THEME.purple} stopOpacity={0.12} />
                  <stop offset="100%" stopColor={THEME.purple} stopOpacity={0.01} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="4 5" vertical={false} stroke="#DDD6FE" strokeOpacity={0.75} />
              <XAxis dataKey="name" interval={0} angle={-18} textAnchor="end" height={64} tick={{ fill: THEME.muted, fontSize: 12 }} />
              <YAxis allowDecimals tick={{ fill: THEME.muted, fontSize: 12 }} width={56} label={{ value: "Minutes", angle: -90, position: "insideLeft", fill: THEME.muted, fontSize: 12 }} />
              <Tooltip content={<ApproverTooltip />} />
              <Area type="monotone" dataKey="averageMinutes" name="Average Minutes" stroke={THEME.purple} strokeWidth={3} fill="url(#approverTimeFill)" dot={{ r: 5, fill: "#fff", stroke: THEME.purple, strokeWidth: 3 }} activeDot={{ r: 7, fill: THEME.purple, stroke: "#fff", strokeWidth: 3 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : <EmptyState />}
    </Card>
  );
};

const TrendChart = ({ title, rows = [], labelKey }) => {
  const data = (Array.isArray(rows) ? rows : []).map((row) => ({ label: clean(row?.[labelKey]), count: toNumber(row?.count), value: toNumber(row?.value) }));
  return (
    <Card title={title}>
      {data.length ? <div style={{ width: "100%", height: 320, borderRadius: 16, padding: "10px 8px 0", background: "linear-gradient(180deg, #EFF6FF 0%, #FFFFFF 100%)", border: "1px solid #DBEAFE" }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 12, left: 0, bottom: 25 }}>
            <defs>
              <linearGradient id={`trendFill-${labelKey}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={THEME.blue} stopOpacity={0.38} />
                <stop offset="55%" stopColor={THEME.cyan} stopOpacity={0.14} />
                <stop offset="100%" stopColor={THEME.cyan} stopOpacity={0.01} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="4 5" vertical={false} stroke="#BFDBFE" strokeOpacity={0.75} />
            <XAxis dataKey="label" tick={{ fill: THEME.muted, fontSize: 11 }} />
            <YAxis tick={{ fill: THEME.muted, fontSize: 12 }} width={54} />
            <Tooltip content={<ChartTooltip />} />
            <Area type="monotone" dataKey="count" name="Count" stroke={THEME.blue} strokeWidth={3} fill={`url(#trendFill-${labelKey})`} dot={{ r: 4, fill: "#fff", stroke: THEME.blue, strokeWidth: 2 }} activeDot={{ r: 6, fill: THEME.blue, stroke: "#fff", strokeWidth: 3 }} />
          </AreaChart>
        </ResponsiveContainer>
      </div> : <EmptyState />}
    </Card>
  );
};

const PeriodTrendPanel = ({ title, monthlyRows = [], yearlyRows = [] }) => {
  const [selectedYear, setSelectedYear] = useState("all");
  const [selectedMonth, setSelectedMonth] = useState("all");

  const years = useMemo(() => Array.from(new Set([
    ...(Array.isArray(yearlyRows) ? yearlyRows.map((row) => clean(row?.year)) : []),
    ...(Array.isArray(monthlyRows) ? monthlyRows.map((row) => clean(row?.month).split("-")[0]) : []),
  ].filter((year) => year && year !== "-"))).sort(), [monthlyRows, yearlyRows]);

  const filteredMonthlyData = useMemo(() => (Array.isArray(monthlyRows) ? monthlyRows : [])
    .filter((row) => {
      const [year, month] = clean(row?.month).split("-");
      return (selectedYear === "all" || year === selectedYear)
        && (selectedMonth === "all" || month === selectedMonth);
    })
    .map((row) => ({
      label: clean(row?.month),
      count: toNumber(row?.count),
      value: toNumber(row?.value),
    })), [monthlyRows, selectedMonth, selectedYear]);

  const yearlyData = useMemo(() => (Array.isArray(yearlyRows) ? yearlyRows : [])
    .map((row) => ({
      label: clean(row?.year),
      count: toNumber(row?.count),
      value: toNumber(row?.value),
    })), [yearlyRows]);

  const visibleCount = filteredMonthlyData.reduce((sum, row) => sum + row.count, 0);
  const selectStyle = {
    minWidth: 140,
    border: `1px solid ${THEME.border}`,
    borderRadius: 10,
    padding: "9px 12px",
    background: "#fff",
    color: THEME.text,
    fontFamily: '"Gilroy-Regular", "Gilroy", "Segoe UI", sans-serif',
    fontSize: 14,
  };

  const renderLineChart = (data, emptyLabel) => data.length ? (
    <div style={{ width: "100%", height: 300, borderRadius: 16, padding: "10px 8px 0", background: "linear-gradient(180deg, #EFF6FF 0%, #FFFFFF 100%)", border: "1px solid #DBEAFE" }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 12, right: 16, left: 0, bottom: 28 }}>
          <defs>
            <linearGradient id="filteredTrendFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={THEME.blue} stopOpacity={0.38} />
              <stop offset="58%" stopColor={THEME.cyan} stopOpacity={0.13} />
              <stop offset="100%" stopColor={THEME.cyan} stopOpacity={0.01} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="4 5" vertical={false} stroke="#BFDBFE" strokeOpacity={0.75} />
          <XAxis dataKey="label" tick={{ fill: THEME.muted, fontSize: 11 }} />
          <YAxis allowDecimals={false} tick={{ fill: THEME.muted, fontSize: 12 }} width={54} />
          <Tooltip content={<ChartTooltip />} />
          <Area type="monotone" dataKey="count" name="Count" stroke={THEME.blue} strokeWidth={3} fill="url(#filteredTrendFill)" dot={{ r: 4, fill: "#fff", stroke: THEME.blue, strokeWidth: 2 }} activeDot={{ r: 6, fill: THEME.blue, stroke: "#fff", strokeWidth: 3 }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  ) : <div style={{ minHeight: 300, display: "grid", placeItems: "center", color: THEME.muted }}>{emptyLabel}</div>;

  return (
    <Card title={title}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <select value={selectedYear} onChange={(event) => setSelectedYear(event.target.value)} style={selectStyle} aria-label="Select PR year">
            <option value="all">All Years</option>
            {years.map((year) => <option key={year} value={year}>{year}</option>)}
          </select>
          <select value={selectedMonth} onChange={(event) => setSelectedMonth(event.target.value)} style={selectStyle} aria-label="Select PR month">
            {MONTH_OPTIONS.map((month) => <option key={month.value} value={month.value}>{month.label}</option>)}
          </select>
        </div>
        <div style={{ padding: "9px 14px", borderRadius: 12, background: "rgba(37,99,235,0.08)", color: THEME.blue, fontSize: 14, fontWeight: 700 }}>
          Selected PR Count: {formatNumber(visibleCount)}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(390px, 1fr))", gap: 18 }}>
        <div style={{ minWidth: 0 }}>
          <h3 style={{ margin: "0 0 8px", color: THEME.text, fontSize: 15 }}>Month-wise PR Count</h3>
          {renderLineChart(filteredMonthlyData, "No monthly data for the selected period.")}
        </div>
        <div style={{ minWidth: 0 }}>
          <h3 style={{ margin: "0 0 8px", color: THEME.text, fontSize: 15 }}>Year-wise PR Count</h3>
          {renderLineChart(yearlyData, "No yearly data available.")}
        </div>
      </div>
    </Card>
  );
};

const POTrendPanel = ({ monthlyRows = [], yearlyRows = [] }) => {
  const years = useMemo(() => Array.from(new Set([
    ...(Array.isArray(yearlyRows) ? yearlyRows.map((row) => clean(row?.year)) : []),
    ...(Array.isArray(monthlyRows) ? monthlyRows.map((row) => getYearFromPeriod(row?.month)) : []),
  ].filter((year) => year && year !== "-"))).sort(), [monthlyRows, yearlyRows]);
  const latestYear = years[years.length - 1] || "all";
  const [selectedYear, setSelectedYear] = useState(latestYear);
  const [selectedMonth, setSelectedMonth] = useState("all");

  useEffect(() => {
    if (!years.length) return;
    if (!selectedYear || !years.includes(selectedYear)) {
      setSelectedYear(latestYear);
    }
  }, [latestYear, selectedYear, years]);

  const monthlyByKey = useMemo(() => {
    const result = {};
    (Array.isArray(monthlyRows) ? monthlyRows : []).forEach((row) => {
      const key = clean(row?.month);
      result[key] = {
        label: key,
        count: toNumber(row?.count),
        value: toNumber(row?.value),
      };
    });
    return result;
  }, [monthlyRows]);

  const filteredMonthlyData = useMemo(() => {
    if (!selectedYear || selectedYear === "all") {
      return (Array.isArray(monthlyRows) ? monthlyRows : []).map((row) => ({
        label: clean(row?.month),
        count: toNumber(row?.count),
        value: toNumber(row?.value),
      }));
    }

    return MONTH_OPTIONS.filter((month) => month.value !== "all").map((month) => {
      const key = `${selectedYear}-${month.value}`;
      const row = monthlyByKey[key] || {};
      return {
        label: month.label,
        count: toNumber(row.count),
        value: toNumber(row.value),
      };
    });
  }, [monthlyByKey, monthlyRows, selectedYear]);

  const filteredYearlyData = useMemo(() => {
    if (selectedMonth === "all") {
      if (Array.isArray(yearlyRows) && yearlyRows.length) {
        return yearlyRows.map((row) => ({
          label: clean(row?.year),
          count: toNumber(row?.count),
          value: toNumber(row?.value),
        }));
      }

      return years.map((year) => {
        const total = MONTH_OPTIONS.filter((month) => month.value !== "all").reduce(
          (result, month) => {
            const row = monthlyByKey[`${year}-${month.value}`] || {};
            result.count += toNumber(row.count);
            result.value += toNumber(row.value);
            return result;
          },
          { count: 0, value: 0 },
        );

        return {
          label: year,
          count: total.count,
          value: total.value,
        };
      });
    }

    return years.map((year) => {
      const row = monthlyByKey[`${year}-${selectedMonth}`] || {};
      return {
        label: year,
        count: toNumber(row.count),
        value: toNumber(row.value),
      };
    });
  }, [monthlyByKey, selectedMonth, yearlyRows, years]);

  const selectStyle = {
    minWidth: 140,
    border: `1px solid ${THEME.border}`,
    borderRadius: 10,
    padding: "9px 12px",
    background: "#fff",
    color: THEME.text,
    fontFamily: '"Gilroy-Regular", "Gilroy", "Segoe UI", sans-serif',
    fontSize: 14,
  };

  const renderLineChart = (data, emptyLabel, gradientId) => data.length ? (
    <div style={{ width: "100%", height: 300, borderRadius: 16, padding: "10px 8px 0", background: "linear-gradient(180deg, #EFF6FF 0%, #FFFFFF 100%)", border: "1px solid #DBEAFE" }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 12, right: 16, left: 0, bottom: 28 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={THEME.blue} stopOpacity={0.38} />
              <stop offset="58%" stopColor={THEME.cyan} stopOpacity={0.13} />
              <stop offset="100%" stopColor={THEME.cyan} stopOpacity={0.01} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="4 5" vertical={false} stroke="#BFDBFE" strokeOpacity={0.75} />
          <XAxis dataKey="label" tick={{ fill: THEME.muted, fontSize: 11 }} />
          <YAxis allowDecimals={false} tick={{ fill: THEME.muted, fontSize: 12 }} width={54} />
          <Tooltip content={<ChartTooltip />} />
          <Area type="monotone" dataKey="count" name="Count" stroke={THEME.blue} strokeWidth={3} fill={`url(#${gradientId})`} dot={{ r: 4, fill: "#fff", stroke: THEME.blue, strokeWidth: 2 }} activeDot={{ r: 6, fill: THEME.blue, stroke: "#fff", strokeWidth: 3 }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  ) : <div style={{ minHeight: 300, display: "grid", placeItems: "center", color: THEME.muted }}>{emptyLabel}</div>;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(390px, 1fr))", gap: 18 }}>
      <Card title="Monthly PO Trend">
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
          <select value={selectedYear} onChange={(event) => setSelectedYear(event.target.value)} style={selectStyle} aria-label="Select PO trend year">
            {years.length ? years.map((year) => <option key={year} value={year}>{year}</option>) : <option value="all">All Years</option>}
          </select>
        </div>
        {renderLineChart(filteredMonthlyData, "No monthly PO data for the selected year.", "monthlyPoTrendFill")}
      </Card>

      <Card title="Yearly PO Trend">
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
          <select value={selectedMonth} onChange={(event) => setSelectedMonth(event.target.value)} style={selectStyle} aria-label="Select PO trend month">
            {MONTH_OPTIONS.map((month) => <option key={month.value} value={month.value}>{month.label}</option>)}
          </select>
        </div>
        {renderLineChart(filteredYearlyData, `No yearly PO data for ${monthLabelByValue[selectedMonth] || "the selected month"}.`, "yearlyPoTrendFill")}
      </Card>
    </div>
  );
};

const EmptyState = () => <div style={{ minHeight: 150, display: "grid", placeItems: "center", color: THEME.muted, fontSize: 14 }}>No data available.</div>;

const Table = ({ title, columns, rows = [] }) => (
  <Card title={title}>
    {rows.length ? <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
        <thead><tr>{columns.map((column) => <th key={column.key} style={{ textAlign: "left", padding: "12px 14px", background: "#F8FAFC", color: "#475569", fontSize: 12, whiteSpace: "nowrap", borderBottom: `1px solid ${THEME.border}` }}>{column.label}</th>)}</tr></thead>
        <tbody>{rows.slice(0, 10).map((row, index) => <tr key={row.token || row.pr_code || index}>{columns.map((column) => <td key={column.key} style={{ padding: "12px 14px", color: THEME.text, fontSize: 13, borderBottom: `1px solid ${THEME.border}`, whiteSpace: column.wrap ? "normal" : "nowrap" }}>{column.render ? column.render(row[column.key], row) : clean(row[column.key])}</td>)}</tr>)}</tbody>
      </table>
    </div> : <EmptyState />}
  </Card>
);

const gridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(390px, 1fr))", gap: 18 };

const PurchaseRequestDashboard = ({ data }) => {
  const navigate = useNavigate();
  const summary = data?.summary || {};
  const approvals = data?.my_approval_summary || {};
  const breakdown = data?.breakdown || {};
  const average = summary?.average_pr_approval_time || {};
  const statusRows = [
    { status: "Approved", count: summary.approved_pr_count },
    { status: "Rejected", count: summary.rejected_pr_count },
    { status: "Pending", count: summary.pending_pr_count },
  ];
  const hasMyApprovalSummary = [
    approvals.assigned_to_me_total,
    approvals.approved_by_me,
    approvals.pending_by_me,
    approvals.rejected_by_me,
  ].some((value) => toNumber(value) > 0);
  const recentApprovalWork = [
    ...(Array.isArray(approvals.pending_by_me_details) ? approvals.pending_by_me_details : []),
    ...(Array.isArray(approvals.approved_by_me_details) ? approvals.approved_by_me_details : []),
    ...(Array.isArray(approvals.rejected_by_me_details) ? approvals.rejected_by_me_details : []),
  ].sort((a, b) => new Date(b?.request_date || 0) - new Date(a?.request_date || 0));
  const yearlyPrRows = Array.isArray(breakdown.year_wise) ? breakdown.year_wise : [];
  const totalPrCountTooltip = (
    <YearWisePrTooltip
      title="Total PR Created"
      total={summary.total_pr_created}
      rows={yearlyPrRows}
      valueKey="count"
    />
  );
  const totalPrValueTooltip = (
    <YearWisePrTooltip
      title="Total PR Value"
      total={summary.total_pr_value}
      rows={yearlyPrRows}
      valueKey="value"
      formatter={formatCurrency}
    />
  );
  return <div style={{ display: "grid", gap: 18 }}>
    <MetricGrid items={[
      { title: "Total PR Created", value: formatNumber(summary.total_pr_created), note: "Unique purchase requests", accent: THEME.blue, tooltip: totalPrCountTooltip, onClick: () => navigate("/User_dashboard/PR") },
      { title: "Total PR Value", value: formatCurrency(summary.total_pr_value), note: "Combined requested value", accent: THEME.purple, tooltip: totalPrValueTooltip },
      { title: "Approved PR", value: formatNumber(summary.approved_pr_count), accent: THEME.green },
      { title: "Pending PR", value: formatNumber(summary.pending_pr_count), accent: THEME.amber },
      { title: "Rejected PR", value: formatNumber(summary.rejected_pr_count), accent: THEME.red },
      { title: "Average Approval Time", value: `${formatNumber(average.average_minutes)} min`, note: `${formatNumber(average.count)} completed approvals`, accent: THEME.cyan },
    ]} />
    <div style={gridStyle}>
      <StatusChart title="PR Status Summary" rows={statusRows} />
      {hasMyApprovalSummary ? (
        <Card title="My Approval Summary"><MetricGrid items={[
          { title: "Assigned", value: formatNumber(approvals.assigned_to_me_total), accent: THEME.blue },
          { title: "Approved", value: formatNumber(approvals.approved_by_me), accent: THEME.green },
          { title: "Pending", value: formatNumber(approvals.pending_by_me), accent: THEME.amber },
          { title: "Rejected", value: formatNumber(approvals.rejected_by_me), accent: THEME.red },
        ]} /></Card>
      ) : null}
    </div>
    <LocationApprovalChart rows={breakdown.location_wise} />
    {hasMyApprovalSummary && recentApprovalWork.length ? (
      <Table title="My Recent Approval Work" rows={recentApprovalWork} columns={[
        { key: "pr_code", label: "PR Code", wrap: true },
        { key: "request_date", label: "Request Date", render: formatDate },
        { key: "requester_name", label: "Requester" },
        { key: "requester_state", label: "State" },
        { key: "requester_country", label: "Country" },
        { key: "status", label: "Status", render: (value) => <span style={{ color: value === "approved" ? THEME.green : value === "rejected" ? THEME.red : THEME.amber, fontWeight: 700, textTransform: "capitalize" }}>{clean(value)}</span> },
      ]} />
    ) : null}
    <ApproverTimeChart rows={summary.approver_time_summary} />
    <div style={gridStyle}>
      <BreakdownChart title="Department-wise PRs" rows={breakdown.department_wise} labelKey="department" />
      <BreakdownChart title="GL Code-wise PRs" rows={breakdown.gl_code_wise} labelKey="gl_code" accent={THEME.purple} />
      <BreakdownChart title="Project-wise PRs" rows={breakdown.project_wise} labelKey="project_code" accent={THEME.green} />
      <BreakdownChart title="Cost Center-wise PRs" rows={breakdown.costcenter_wise} labelKey="cost_center" accent={THEME.amber} />
      <BreakdownChart title="Most Raised Items" rows={breakdown.most_items_raised} labelKey="item_name" accent={THEME.cyan} />
    </div>
    <PeriodTrendPanel title="PR Creation Trend" monthlyRows={breakdown.month_wise} yearlyRows={breakdown.year_wise} />
    <Table title="Recently Raised Items" rows={breakdown.recent_items_raised || []} columns={[
      { key: "pr_code", label: "PR Code", wrap: true }, { key: "request_date", label: "Request Date", render: formatDate },
      { key: "item_name", label: "Item" }, { key: "item_number", label: "Item Number" }, { key: "quantity", label: "Quantity" },
      { key: "value", label: "Value", render: formatCurrency }, { key: "department", label: "Department" }, { key: "gl_code", label: "GL Code" },
      { key: "project_code", label: "Project" }, { key: "cost_center", label: "Cost Center" },
    ]} />
  </div>;
};

const MatchingDashboard = ({ data }) => {
  const navigate = useNavigate();
  const summary = data?.summary || {};
  const breakdown = data?.breakdown || {};
  return <div style={{ display: "grid", gap: 18 }}>
    <MetricGrid items={[
      { title: "Total Matching Runs", value: formatNumber(summary.total_runs), accent: THEME.blue, onClick: () => navigate("/User_dashboard/Matching") },
      { title: "Invoices Processed", value: formatNumber(summary.total_invoices_processed), accent: THEME.cyan },
      { title: "Fully Matched Invoices", value: formatNumber(summary.fully_matched_invoices), accent: THEME.green },
      { title: "Fully Matched Runs", value: formatNumber(summary.fully_matched_runs), accent: THEME.purple },
      { title: "Fully Matched Rate", value: `${formatNumber(summary.fully_matched_rate)}%`, accent: THEME.amber },
    ]} />
    <div style={gridStyle}>
      <StatusChart title="Matching Status" rows={breakdown.status_wise || []} />
      <BreakdownChart title="Mismatch Reasons" rows={breakdown.mismatch_reasons} labelKey="reason" accent={THEME.red} />
    </div>
    <Table title="Recent Matching Runs" rows={breakdown.recent_runs || []} columns={[
      { key: "twm_code", label: "TWM Code" }, { key: "status", label: "Status" },
      { key: "created_at", label: "Created", render: formatDate }, { key: "last_updated_at", label: "Last Updated", render: formatDate },
    ]} />
  </div>;
};

const PurchaseOrderDashboard = ({ data }) => {
  const navigate = useNavigate();
  const summary = data?.summary || {};
  const approval = data?.po_approval_breakdown || {};
  const breakdown = data?.breakdown || {};
  const approvalRows = Object.entries(approval).map(([status, count]) => ({ status, count }));
  const stateRows = Object.entries(breakdown.state_wise || {}).map(([status, count]) => ({ status, count }));
  const poDetailRows = useMemo(() => {
    const candidateLists = [
      breakdown.po_details,
      breakdown.purchase_orders,
      breakdown.po_list,
      breakdown.po_details_list,
    ];

    return candidateLists.find((rows) => Array.isArray(rows) && rows.length) || [];
  }, [breakdown]);
  const yearlyPoRowsFromDetails = useMemo(() => {
    const grouped = {};

    poDetailRows.forEach((row) => {
      const year = getYearFromDate(
        row?.purchase_order_date ||
          row?.po_date ||
          row?.created_at ||
          row?.created_date,
      );
      if (!year) return;

      if (!grouped[year]) grouped[year] = { year, count: 0, value: 0 };
      grouped[year].count += 1;
      grouped[year].value += toNumber(
        row?.po_value ??
          row?.value ??
          row?.total_value ??
          row?.total_po_value,
      );
    });

    const rowsFromDetails = Object.values(grouped).sort((a, b) =>
      String(a.year).localeCompare(String(b.year)),
    );

    return rowsFromDetails;
  }, [poDetailRows]);
  const yearlyPoRows = useMemo(() => {
    const rowsFromBackend = (Array.isArray(breakdown.year_wise) ? breakdown.year_wise : []).map((row) => ({
      year: clean(row?.year),
      count: toNumber(row?.count),
      value: toNumber(
        row?.value ??
          row?.po_value ??
          row?.total_value ??
          row?.total_po_value,
      ),
    }));

    return rowsFromBackend.length ? rowsFromBackend : yearlyPoRowsFromDetails;
  }, [breakdown.year_wise, yearlyPoRowsFromDetails]);
  const derivedMonthlyPoRows = useMemo(() => {
    if (Array.isArray(breakdown.month_wise) && breakdown.month_wise.length) {
      return breakdown.month_wise;
    }

    const grouped = {};
    poDetailRows.forEach((row) => {
      const rawDate =
        row?.purchase_order_date ||
        row?.po_date ||
        row?.created_at ||
        row?.created_date;
      const date = new Date(rawDate);
      if (Number.isNaN(date.getTime())) return;

      const month = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      if (!grouped[month]) grouped[month] = { month, count: 0, value: 0 };
      grouped[month].count += 1;
      grouped[month].value += toNumber(
        row?.po_value ??
          row?.value ??
          row?.total_value ??
          row?.total_po_value,
      );
    });

    return Object.values(grouped).sort((a, b) => String(a.month).localeCompare(String(b.month)));
  }, [breakdown.month_wise, poDetailRows]);
  const derivedYearlyPoRows = useMemo(() => {
    if (Array.isArray(breakdown.year_wise) && breakdown.year_wise.length) {
      return yearlyPoRows;
    }

    return yearlyPoRows;
  }, [breakdown.year_wise, yearlyPoRows]);
  const totalPoCreatedTooltip = (
    <YearWiseSummaryTooltip
      title="Total PO Created"
      total={summary.total_po_created}
      rows={yearlyPoRows}
      valueKey="count"
    />
  );
  const totalPoValueTooltip = (
    <YearWiseSummaryTooltip
      title="Total PO Value"
      total={summary.total_po_value}
      rows={yearlyPoRows}
      valueKey="value"
      formatter={formatCurrency}
    />
  );

  return <div style={{ display: "grid", gap: 18 }}>
    <MetricGrid items={[
      { title: "Total PO Created", value: formatNumber(summary.total_po_created), accent: THEME.blue, tooltip: totalPoCreatedTooltip, onClick: () => navigate("/User_dashboard/PO") },
      { title: "Total PO Value", value: formatCurrency(summary.total_po_value), accent: THEME.purple, tooltip: totalPoValueTooltip },
      { title: "Paid Amount", value: formatCurrency(summary.paid_amount), accent: THEME.green },
      { title: "Outstanding Amount", value: formatCurrency(summary.outstanding_amount), accent: THEME.amber },
    ]} />
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 18 }}>
      <StatusChart title="PO Approval Status" rows={approvalRows} />
      <StatusChart title="PO State" rows={stateRows} />
    </div>
    <div style={gridStyle}>
      <BreakdownChart title="Vendor-wise PO Value" rows={breakdown.vendor_wise} labelKey="vendor_name" valueKey="value" valueName="Value" accent={THEME.purple} />
      <BreakdownChart title="Item-wise PO Value" rows={breakdown.item_wise} labelKey="item_name" valueKey="value" valueName="Value" accent={THEME.green} />
    </div>
    <POTrendPanel monthlyRows={derivedMonthlyPoRows} yearlyRows={derivedYearlyPoRows} />
    <Table title="Recent Purchase Orders" rows={breakdown.recent_pos || []} columns={[
      { key: "po_code", label: "PO Code" }, { key: "vendor_name", label: "Vendor" }, { key: "status", label: "Status" },
      { key: "value", label: "Value", render: formatCurrency }, { key: "created_at", label: "Created", render: formatDate },
    ]} />
  </div>;
};

const UserDashboard = ({ dashboardResponse = {}, loadingState = false }) => {
  const root = dashboardResponse?.response || dashboardResponse || {};
  const [activeSection, setActiveSection] = useState("dashboard1");
  const activeData = root?.[activeSection] || {};
  const user = root?.user || {};

  return (
    <div className={classes.dashboardPage} style={{ background: "radial-gradient(circle at 8% 0%, rgba(37,99,235,0.08), transparent 28%), radial-gradient(circle at 92% 8%, rgba(124,58,237,0.07), transparent 24%), #F6F8FC", color: THEME.text, fontFamily: '"Gilroy-Regular", "Gilroy", "Segoe UI", sans-serif' }}>
      <div className={classes.pageSectionPadMain} style={{ paddingTop: 20 }}>
        <div style={{ position: "relative", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16, marginBottom: 18, padding: "22px 24px", borderRadius: 22, background: "linear-gradient(118deg, #FFFFFF 0%, #EEF4FF 55%, #F5F0FF 100%)", border: "1px solid rgba(165,180,252,0.45)", boxShadow: "0 12px 34px rgba(37,99,235,0.09)" }}>
          <div style={{ position: "absolute", width: 180, height: 180, borderRadius: "50%", right: -55, top: -100, background: "rgba(124,58,237,0.08)" }} />
          <div style={{ position: "relative" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "5px 10px", borderRadius: 999, color: THEME.blue, background: "rgba(37,99,235,0.09)", fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8 }}><span style={{ width: 7, height: 7, borderRadius: "50%", background: THEME.blue }} />Live analytics</div>
            <div style={{ color: "#173B7A", fontSize: "clamp(24px, 2.4vw, 32px)", fontFamily: '"Gilroy-SemiBold", "Gilroy", "Segoe UI", sans-serif', fontWeight: 600, letterSpacing: "-0.025em" }}>Procurement Command Center</div>
            <div style={{ color: THEME.muted, marginTop: 5 }}>{user.user_name ? `Welcome, ${user.user_name}` : "Procurement analytics overview"}{user.org_name ? ` | ${user.org_name}` : ""}</div>
          </div>
          <div style={{ position: "relative", padding: "10px 14px", borderRadius: 13, color: "#5B21B6", background: "rgba(124,58,237,0.08)", border: "1px solid rgba(124,58,237,0.12)", fontSize: 12, fontWeight: 600 }}>User Dashboard</div>
        </div>
        <div className={classes.tabNavInner} style={{ marginBottom: 18, position: "sticky", top: 12, zIndex: 8 }}>
          <div className={classes.tabNavLabel}>DASHBOARD SECTIONS</div>
          <div className={classes.tabNavTabs}>
            {MENU.map((tab) => <button key={tab.id} type="button" onClick={() => setActiveSection(tab.id)} className={`${classes.tabBtn} ${activeSection === tab.id ? classes.tabBtnActive : ""}`} style={{ "--tab-accent": tab.accent, scrollSnapAlign: "start" }}>
              <div className={classes.tabBtnTop}><span className={classes.tabBtnDot} /><span className={classes.tabBtnLabel}>{tab.label}</span></div>
              <div className={classes.tabBtnDesc}>{tab.description}</div>
            </button>)}
          </div>
        </div>
        {loadingState ? <Card title="Loading Dashboard"><EmptyState /></Card> : activeSection === "dashboard1" ? <PurchaseRequestDashboard data={activeData} /> : activeSection === "dashboard2" ? <MatchingDashboard data={activeData} /> : <PurchaseOrderDashboard data={activeData} />}
      </div>
    </div>
  );
};

export default UserDashboard;
